import { Controller } from "stimulus";

const csrfToken = (): string => document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || "";

export default class extends Controller {
  static targets = ["result", "modal", "summary", "nameRow", "name", "error", "confirm"];
  static values = { previewUrl: String, clearUrl: String };

  declare readonly resultTarget: HTMLElement;
  declare readonly modalTarget: HTMLElement;
  declare readonly summaryTarget: HTMLElement;
  declare readonly nameRowTarget: HTMLElement;
  declare readonly nameTarget: HTMLInputElement;
  declare readonly errorTarget: HTMLElement;
  declare readonly confirmTarget: HTMLButtonElement;
  declare previewUrlValue: string;
  declare clearUrlValue: string;

  private params: Record<string, string> = {};
  private fingerprint = "";
  private botName = "";

  async preview(event: Event): Promise<void> {
    const button = event.currentTarget as HTMLElement;
    const scope = button.dataset.scope || "candidate";
    this.element.querySelectorAll<HTMLElement>("[data-scope]").forEach((chip) => chip.setAttribute("aria-pressed", String(chip === button)));
    const { botId = "", bpId = "" } = (this.element as HTMLElement).dataset;
    if (scope === "candidate" && !bpId) return this.say("Pick a candidate first.");
    if (scope === "campaign" && !botId) return this.say("Pick a campaign first.");
    this.params = scope === "candidate" ? { scope, bots_prospect_id: bpId } : { scope, bot_id: botId };
    const url = new URL(this.previewUrlValue, window.location.origin);
    Object.entries(this.params).forEach(([key, value]) => url.searchParams.set(key, value));
    this.say("Counting the history…");
    try {
      const response = await fetch(url.toString(), { headers: { Accept: "text/html", "X-Requested-With": "XMLHttpRequest" }, credentials: "same-origin" });
      if (!response.ok) throw new Error(String(response.status));
      this.resultTarget.innerHTML = await response.text();
    } catch {
      this.say("Couldn't count the history. Try again.");
    }
  }

  openModal(): void {
    const preview = this.resultTarget.querySelector<HTMLElement>(".tt-history-preview");
    if (!preview) return;
    this.fingerprint = preview.dataset.fingerprint || "";
    this.botName = preview.dataset.botName || "";
    const campaign = preview.dataset.scope === "campaign";
    const rows = Array.from(preview.querySelectorAll("tbody .tt-history-campaign")).map((name) => name.textContent?.trim()).filter(Boolean);
    this.summaryTarget.textContent = `${preview.dataset.chains} chains with their emails, replies and forwards will be deleted in: ${rows.join(", ")}. The candidates go back in the send queue. This can't be undone.`;
    this.nameRowTarget.hidden = !campaign;
    this.nameTarget.value = "";
    this.errorTarget.hidden = true;
    this.confirmTarget.disabled = campaign;
    this.modalTarget.classList.add("open");
  }

  closeModal(): void {
    this.modalTarget.classList.remove("open");
  }

  checkName(): void {
    this.confirmTarget.disabled = this.nameTarget.value.trim() !== this.botName.trim();
  }

  async clear(): Promise<void> {
    this.confirmTarget.disabled = true;
    const body = new URLSearchParams(this.params);
    body.set("fingerprint", this.fingerprint);
    if (!this.nameRowTarget.hidden) body.set("confirm_name", this.nameTarget.value);
    try {
      const response = await fetch(this.clearUrlValue, {
        method: "DELETE",
        credentials: "same-origin",
        headers: { "X-CSRF-Token": csrfToken(), Accept: "application/json", "X-Requested-With": "XMLHttpRequest", "Content-Type": "application/x-www-form-urlencoded" },
        body,
      });
      const reply = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(reply.error || `The history couldn't be cleared (${response.status}).`);
      this.closeModal();
      this.say(`Cleared ${reply.chains} chains (${reply.emails} emails) for ${reply.candidates} candidates; they're back in the send queue.`);
      this.element.dispatchEvent(new CustomEvent("testing-history-clear:cleared", { bubbles: true }));
    } catch (error) {
      this.errorTarget.textContent = (error as Error).message;
      this.errorTarget.hidden = false;
      this.confirmTarget.disabled = false;
    }
  }

  private say(text: string): void {
    this.resultTarget.innerHTML = "";
    const line = document.createElement("p");
    line.className = "muted";
    line.textContent = text;
    this.resultTarget.appendChild(line);
  }
}
